/**
 * Shared citation token normalizers for render + gen-time scrub passes.
 */

/**
 * `[Ref: 12]` / `[Ref:12]` — the COLON token form the model also emits.
 *
 * THIS WAS SILENT CITATION CORRUPTION (2026-08-14). Every ref regex in the
 * platform is `/\[Ref\s+(\d+)\]/` — `\s+` demands whitespace, and a colon is
 * not whitespace — so the colon form was invisible to the auditor, the
 * back-ref builder, the out-of-bounds scrub, `reportCitedDocIds`, and most
 * damagingly `renumberReferences`. Proven by running the real renumberer:
 *
 *   before  "...B [Ref 2] and C [Ref 3]. ...B [Ref: 2] and C [Ref: 3]."
 *   after   "...B [Ref 1] and C [Ref 2]. ...B [Ref: 2] and C [Ref: 3]."
 *   refs    Source B | Source C | Source A
 *
 * The visible tokens were compacted; the colon tokens kept their OLD indices,
 * so `[Ref: 2]` now resolves to Source C when it cited Source B. Nothing
 * errors — the citation simply points at the wrong source. On one live
 * feasibility body 111 of 213 citations were in this form.
 *
 * Normalising the punctuation ONCE, at the generation boundary and before
 * renumbering, makes every existing consumer work unchanged.
 */
const COLON_REF_RE = /\[Ref\s*:\s*(\d+)\]/gi;

/** Complete numbered citation grammar, shared by source-order collection and
 * renumbering. Reliability labels stay attached to the same source; ordinary
 * bracketed numbers, ranges, qualifiers and unknown markers are not citations.
 * Consumers copy this regex before scanning so its global cursor is isolated. */
export const NUMBERED_REF_TOKEN_RE =
  /\[(?:(?:verified|inferred|unverified)\s+)?Ref\s*(?::\s*)?\d+(?:\s*,\s*(?:(?:(?:verified|inferred|unverified)\s+)?Ref\s*(?::\s*)?)?\d+)*\]/gi;

/** Resolve complete citation tokens without interpreting their reliability
 * labels as evidence authority. Each consumer must validate the source role. */
export function extractNumberedRefIds(text: string): number[] {
  const ids = [...text.matchAll(new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi'))]
    .flatMap(token => [...token[0].matchAll(/\d+/g)].map(number => Number(number[0])))
    .filter(id => Number.isSafeInteger(id) && id > 0);
  return [...new Set(ids)];
}

/** Hide citation metadata from claim-word analysis while preserving offsets. */
export function maskNumberedRefTokens(text: string): string {
  return text.replace(new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi'), token => ' '.repeat(token.length));
}


/**
 * Fold the colon token form into the canonical `[Ref N]` spelling.
 * Pure + total; idempotent; leaves canonical tokens untouched.
 */
export function normalizeRefTokenPunctuation(text: string): string {
  if (!text) return text ?? '';
  return text.replace(COLON_REF_RE, (_full, n: string) => `[Ref ${n}]`);
}

const COMPOUND_REF_RE = /\[Ref\s*(?::\s*)?\d+(?:\s*,\s*(?:Ref\s*(?::\s*)?)?\d+)+\]/gi;

const REF_QUALIFICATION_RANK: Readonly<Record<string, number>> = {
  unverified: 3,
  inferred: 2,
  verified: 1,
  '': 0,
};

/**
 * Conservatively select the stronger uncertainty/qualification label when two
 * inline citations to the same reference index collapse (`unverified` >
 * `inferred` > `verified` > unqualified), regardless of token ordering.
 */
export function pickStrongerRefQualification(
  a: string | undefined,
  b: string | undefined,
): string {
  const qa = (a ?? '').trim().toLowerCase();
  const qb = (b ?? '').trim().toLowerCase();
  return (REF_QUALIFICATION_RANK[qb] ?? 0) > (REF_QUALIFICATION_RANK[qa] ?? 0) ? qb : qa;
}

const CONTIGUOUS_REF_CLUSTER_RE =
  /\[(?:(verified|inferred|unverified)\s+)?Ref\s+(\d+)\](?:\s*[,;]?\s*\[(?:(verified|inferred|unverified)\s+)?Ref\s+(\d+)\])+/gi;

/**
 * Collapse duplicate `[Ref N]` tokens within any contiguous citation cluster
 * (e.g. `[Ref 1] [unverified Ref 1]`, `[unverified Ref 1], [Ref 1]`, or
 * `[Ref 5], [Ref 16], [Ref 30], [Ref 5]`) into a deduplicated sequence while
 * preserving first-seen order and the strongest uncertainty/qualification
 * across all collapsed tokens for each reference number.
 */
export function collapseAdjacentDuplicateRefTokens(text: string): string {
  if (!text) return text ?? '';
  return text.replace(CONTIGUOUS_REF_CLUSTER_RE, (full) => {
    const matches = [...full.matchAll(/\[(?:(verified|inferred|unverified)\s+)?Ref\s+(\d+)\]/gi)];
    if (matches.length <= 1) return full;
    const byRefNum = new Map<string, string>();
    for (const m of matches) {
      const refNum = m[2]!;
      const prevQual = byRefNum.get(refNum);
      byRefNum.set(refNum, pickStrongerRefQualification(prevQual, m[1]));
    }
    if (byRefNum.size === matches.length) {
      return full;
    }
    const separator = /\]\s*[,;]\s*\[/.test(full) ? ', ' : ' ';
    return [...byRefNum.entries()]
      .map(([refNum, qual]) => `[${qual ? `${qual} ` : ''}Ref ${refNum}]`)
      .join(separator);
  });
}

/**
 * Expand compound ref tokens like `[Ref 21, Ref 42]` or `[Ref 21, 42]` into separate
 * linkable tokens: `[Ref 21], [Ref 42]`.
 */
export function normalizeCompoundRefTokens(text: string): string {
  if (!text) return text ?? '';
  return collapseAdjacentDuplicateRefTokens(
    text.replace(COMPOUND_REF_RE, (full) => {
      // The complete bracket must match first, so ordinary numeric lists,
      // ranges and malformed citations cannot become source links.
      const nums = [...new Set([...full.matchAll(/\d+/g)].map(m => m[0]))];
      return nums.map(n => `[Ref ${n}]`).join(', ');
    }),
  );
}

/** True when prose already contains one or more [Ref N] appendix tokens. */
export function sentenceHasInlineRefTokens(sentence: string): boolean {
  return /\[Ref\s+\d+\]/i.test(sentence);
}

/**
 * Unresolvable citation placeholders — `[Ref ?]` (gen-side out-of-bounds
 * scrub legacy form) and the compact `[?]` — with any leading whitespace so
 * removal never leaves a doubled space ("word [Ref ?]." → "word.").
 */
const UNRESOLVABLE_REF_RE = /[ \t]*\[\s*(?:Ref\s+)?\?\s*\]/gi;

/**
 * Redhead review wave 3 (defect 1) — REMOVE unresolvable citation
 * placeholders from display text instead of ever rendering a literal
 * `[Ref ?]` / `[?]`. A citation that cannot resolve carries no reader value;
 * the removal is LOGGED (console.warn) so the degradation is never silent.
 *
 * Pure + total. `context` names the calling surface for the log line.
 */
export function stripUnresolvableRefTokens(text: string, context?: string): string {
  if (!text || !text.includes('?]')) return text ?? '';
  let count = 0;
  const out = text.replace(UNRESOLVABLE_REF_RE, () => {
    count += 1;
    return '';
  });
  if (count > 0) {
    console.warn(
      `[citations] removed ${count} unresolvable citation placeholder(s)`
      + `${context ? ` (${context})` : ''} — token could not resolve to an appendix row`,
    );
  }
  return out;
}

/**
 * Redhead review wave 3 (defect 1) — textually REMOVE `[Ref N]` tokens the
 * caller resolves as out-of-bounds (hallucinated N, or appendix row filtered
 * out), consuming the leading whitespace so no doubled space survives. Doing
 * this BEFORE the render split keeps the surrounding prose seamless (a
 * removed mid-sentence token leaves exactly one space), where suppressing
 * the pill at the component level would leave the two flanking text nodes'
 * spaces both in the DOM. Logged — removal is never silent.
 */
export function removeOutOfBoundsRefTokens(
  text: string,
  isOutOfBounds: (n: number) => boolean,
  context?: string,
): string {
  if (!text || !text.includes('[')) return text ?? '';
  let count = 0;
  const out = text.replace(/[ \t]*\[Ref\s+(\d+)\]/gi, (full, numStr: string) => {
    const n = parseInt(numStr, 10);
    if (Number.isFinite(n) && isOutOfBounds(n)) {
      count += 1;
      return '';
    }
    return full;
  });
  if (count > 0) {
    console.warn(
      `[citations] removed ${count} out-of-bounds citation marker(s)`
      + `${context ? ` (${context})` : ''} — no matching appendix row`,
    );
  }
  return out;
}

/**
 * Redhead review wave 3 (defect 6) — repair MISSING SPACE after an inline
 * citation token: prose like "availability [Ref 2].A written quote" (or
 * "COD [Ref 38].While") renders as "…[2].A…" with the next sentence glued to
 * the citation period. Insert exactly one space between the token's trailing
 * punctuation (if any) and a following letter:
 *
 *   "[Ref 2].A"  → "[Ref 2]. A"
 *   "[Ref 38],b" → "[Ref 38], b"
 *   "[Ref 4]The" → "[Ref 4] The"
 *
 * Deliberately citation-scoped (never a blanket punctuation-spacing pass, so
 * decimals / URLs / file paths elsewhere in the prose are untouched). The
 * lookahead requires a LETTER or "(" so "[Ref 2]." at end-of-string, before
 * whitespace, or before a digit is left exactly as-is. Handles the optional
 * reliability-tier prefix (`[verified Ref 3]`) too.
 */
const REF_TOKEN_MISSING_SPACE_RE =
  /(\[(?:(?:verified|inferred|unverified)\s+)?Ref\s+\d+\])([.,;:!?]*)(?=[A-Za-z(])/gi;

export function ensureSpaceAfterRefTokens(text: string): string {
  if (!text || !text.includes('[')) return text ?? '';
  return text.replace(REF_TOKEN_MISSING_SPACE_RE, (_m, tok: string, punct: string) => `${tok}${punct} `);
}

/**
 * CITE-CAP-1 (2026-07-03) — cap REPEATED `[Ref N]` pills per string field.
 *
 * The citation-density enforcement pushed the generator into citing the same
 * reference on every mention: the live Redhead regen carried 81 pills in ONE
 * section (corpus p10–p90 = 3–30) and tripped the distribution hold. Density
 * is computed over `claims[]` (not prose pills), so capping prose repeats
 * CANNOT lower the density score — it only removes redundant click targets.
 *
 * Within one string field, the first `maxPerRef` occurrences of each ref
 * number are kept (reliability-prefixed forms count toward the same N);
 * later repeats are removed with their leading whitespace, and token
 * spacing is repaired. Distinct refs are never affected.
 */
const ANY_REF_TOKEN_RE = /[ \t]*\[(?:(?:verified|inferred|unverified)\s+)?Ref\s+(\d+)\]/gi;

export function capRepeatedRefTokens(text: string, maxPerRef = 3): { text: string; removed: number } {
  if (!text || !text.includes('[')) return { text: text ?? '', removed: 0 };
  const normalized = normalizeCompoundRefTokens(text);
  const counts = new Map<number, number>();
  let removed = 0;
  const capped = normalized.replace(ANY_REF_TOKEN_RE, (full, group: string) => {
    const n = parseInt(group, 10);
    if (Number.isNaN(n)) return full;
    const seen = (counts.get(n) ?? 0) + 1;
    counts.set(n, seen);
    if (seen > maxPerRef) {
      removed++;
      return '';
    }
    return full;
  });
  return { text: removed > 0 ? ensureSpaceAfterRefTokens(capped) : normalized, removed };
}
